import { NgIf, NgFor } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EscolaComponent } from '../escola/escola.component';
import { mask_porcentagem_limpa } from '../../directives/mascaras';
import { LayoutEditorContentService, IconeAtivo } from '../../layouts/layout-editor/layout-editor-content.service';
import { AcessoOfertaEducacionalStateService, EtapasDeEdicaoService } from "../../services";
import {
  corFundoLateral,
  tituloMenuLateral,
  caminhoImagemLateral,
  opcoesMenusLateralAtivas,
} from '../../layouts/layout-editor/template/lateral-navegacao-acesso-oferta-educacional';
import { ProjecaoOfertaMatriculas } from '../../dtos';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-projecao-da-oferta',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    EscolaComponent,
  ],
  templateUrl: './projecao-da-oferta.component.html',
  styleUrl: './projecao-da-oferta.component.scss',
})
export class ProjecaoDaOfertaComponent implements OnInit {
  projecoes: ProjecaoOfertaMatriculas[] = [];
  projecoesFiltradas: ProjecaoOfertaMatriculas[] = [];
  termoBusca = '';
  // #1128: id da escola com o accordion aberto (abrir "um por vez")
  escolaAbertaId: number | null = null;
  mask_porcentagem_limpa = mask_porcentagem_limpa;

  private readonly comparador = new Intl.Collator('pt-BR', {
    usage: 'sort',
    sensitivity: 'base',
    ignorePunctuation: true,
    numeric: true,
  });

  trackByEscola = (_: number, p: ProjecaoOfertaMatriculas) => p.codMec ?? p.nomeInstituicao;

  letras: readonly string[] = Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ#');

  letrasComEscola = new Set<string>();

  private primeiroIndicePorLetra = new Map<string, number>();

  // referência para rolagem dentro da lista
  @ViewChildren('escolaAnchor', { read: ElementRef }) private ancorasEscolas!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('listaContainer', { read: ElementRef }) private containerLista!: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private etapasDeEdicaoService: EtapasDeEdicaoService,
    private layoutEditorContentService: LayoutEditorContentService,
    private acessoOfertaEducacionalStateService: AcessoOfertaEducacionalStateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.etapasDeEdicaoService.setOpcaoAtual(2);

    this.layoutEditorContentService.definirIconeAtivo(IconeAtivo.ANDARILHO);
    this.layoutEditorContentService.definirTituloMenuLateral(tituloMenuLateral);
    this.layoutEditorContentService.definirCorFundoMenuLateral(corFundoLateral);
    this.layoutEditorContentService.definirOpcoesAtivas(opcoesMenusLateralAtivas);
    this.layoutEditorContentService.definirCaminhoImagem(caminhoImagemLateral);
    this.layoutEditorContentService.definirOpcoesInativas([]);
    this.layoutEditorContentService.definiOpcaoAtivaSelecionada(1);
    this.layoutEditorContentService.definirExibirBotaoAnterior(true);
    this.layoutEditorContentService.definirRotaAnterior('/relatorio2/acesso-oferta-educacional/diagnostico-matricula');
    this.layoutEditorContentService.definirExibirBotaoProximo(true);
    this.layoutEditorContentService.definirRotaProximo('/relatorio2/acesso-oferta-educacional/resultado-projecao');
  }

  ngOnInit(): void {
    if (this.acessoOfertaEducacionalStateService.carregando.getValue()) {
      this.acessoOfertaEducacionalStateService.carregando.subscribe(() => {
        this.carregarDados();
        return;
      });
    }

    this.carregarDados();
  }

  private carregarDados(): void {
    const dados = this.acessoOfertaEducacionalStateService.getProjecoesOfertaMatriculasValores();
    this.projecoes = this.ordenarPorNome(dados);
    this.reconstruirIndiceAlfabetico();
    this.aplicarFiltro();
    this.cdr.markForCheck();
  }

  // #1128: filtro basico das escolas pelo nome (sem acento/caixa)
  aplicarFiltro(): void {
    const termo = this.normalizarTexto(this.termoBusca);
    this.projecoesFiltradas = termo
      ? this.projecoes.filter(p => this.normalizarTexto(p?.nomeInstituicao ?? '').includes(termo))
      : this.projecoes;
    this.cdr.markForCheck();
  }

  // #1128: mantem apenas um accordion aberto por vez
  aoAlternarEscola(aberto: boolean, projecao: ProjecaoOfertaMatriculas): void {
    this.escolaAbertaId = aberto ? projecao.codMec : null;
  }

  private normalizarTexto(texto: string): string {
    return (texto ?? '')
      .normalize('NFD').replace(/\p{Diacritic}/gu, '')
      .toLowerCase().trim();
  }

  private ordenarPorNome(lista: ProjecaoOfertaMatriculas[]): ProjecaoOfertaMatriculas[] {
    return [...lista].sort((a, b) => {
      const n1 = a?.nomeInstituicao ?? '';
      const n2 = b?.nomeInstituicao ?? '';
      return this.comparador.compare(n1, n2) || ((a?.codMec ?? 0) - (b?.codMec ?? 0));
    });
  }

  private reconstruirIndiceAlfabetico(): void {
    this.letrasComEscola.clear();
    this.primeiroIndicePorLetra.clear();

    for (let i = 0; i < this.projecoes.length; i++) {
      const letra = this.primeiraLetraNormalizada(this.projecoes[i]?.nomeInstituicao ?? '');
      if (!this.primeiroIndicePorLetra.has(letra)) this.primeiroIndicePorLetra.set(letra, i);
      this.letrasComEscola.add(letra);
    }
  }

  // normaliza o nome e extrai a primeira letra A..Z (ou retorna #)
  private primeiraLetraNormalizada(nome: string): string {
    const base = (nome || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
      .toUpperCase().trim();
    const match = base.match(/[A-Z]/);
    return match ? match[0] : '#';
  }

  rolarParaLetra(letra: string): void {
    const idx = this.primeiroIndicePorLetra.get(letra);
    if (idx == null) return;

    const item = this.ancorasEscolas?.toArray()[idx]?.nativeElement;
    const container = this.containerLista?.nativeElement;
    if (!item || !container) return;

    // rola explicitamente o container da lista
    const rectContainer = container.getBoundingClientRect();
    const rectItem = item.getBoundingClientRect();
    const delta = rectItem.top - rectContainer.top;

    container.scrollTo({
      top: container.scrollTop + delta - 8,
      behavior: 'smooth',
    });
  }

}
